import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import SampleType from '../../../../types/sample-type/SampleType';
import SampleTypeWriteDTO from '../../../../types/sample-type/SampleTypeWriteDTO';
import Page from '../../../../types/shared/Page';
import { CanManageSampleDefinitionsDirective } from '../../directives/can-manage-sample-definitions.directive';
import { ExportService } from '../../services/export.service';
import { SampleTypeService } from '../../services/sample-type.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import {
    CreateSampleTypeDialogComponent
} from '../shared/sample-types/create-sample-type-dialog/create-sample-type-dialog.component';
import {
    UpdateSampleTypeDialogComponent
} from '../shared/sample-types/update-sample-type-dialog/update-sample-type-dialog.component';

@Component({
    selector: 'app-sample-types',
    templateUrl: './sample-types.component.html',
    styleUrls: [ './sample-types.component.scss' ],
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        CreateSampleTypeDialogComponent,
        UpdateSampleTypeDialogComponent,
        InputText,
        RouterLink,
        IftaLabelModule,
        CanManageSampleDefinitionsDirective,
        Tooltip
    ]
})
export class SampleTypesComponent implements OnInit {
    public search = '';
    public sampleTypes: SampleType[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateSampleTypeDialog = false;
    public displayUpdateSampleTypeDialog = false;
    public selectedSampleType: SampleTypeWriteDTO = new SampleTypeWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(private sampleTypeService: SampleTypeService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private exportService: ExportService) {
    }

    /**
     * Initializes the component and loads the sample types.
     */
    public ngOnInit(): void {
        this.loadSampleTypes();
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0;
            this.loadSampleTypes();
        });
    }

    /**
     * Loads the sample types from the server.
     */
    public loadSampleTypes(): void {
        this.loading = true;
        const size = this.rows;
        const search = this.search || '';

        this.sampleTypeService
            .getSampleTypes(search, this.currentPage, size)
            .then((result: Page<SampleType>) => {
                this.sampleTypes = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Handles the page change event.
     *
     * @param event The page change event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadSampleTypes();
    }

    /**
     * Handles the search input change event.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Opens the edit sample type dialog.
     */
    public editSampleType(sampleType: SampleType): void {
        this.selectedSampleType = new SampleTypeWriteDTO(sampleType);
        this.displayUpdateSampleTypeDialog = true;
    }

    /**
     * Deletes a sample type after user confirmation.
     *
     * @param sampleType The sample type to delete.
     */
    public deleteSampleType(sampleType: SampleType): void {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the sample type ' + sampleType.name + '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.sampleTypeService.deleteSampleType(sampleType.id).then(() => {
                    this.loadSampleTypes();
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Sample type deleted', life: 3000 });
                }).catch((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Opens the dialog to create a new sample type.
     */
    public openCreateSampleTypeDialog(): void {
        this.displayCreateSampleTypeDialog = true;
    }

    /**
     * Reloads the sample types.
     */
    public reloadSampleTypes(): void {
        this.loadSampleTypes();
    }

    /**
     * Exports the sample types to a file.
     */
    public exportSampleTypes() {
        this.exportService.exportSampleTypes().then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Sample types exported successfully',
                life: 3000,
            });
        }).catch((error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
        });
    }
}
